import React from 'react'
import { useNavigate, Routes, Route, useParams, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Welcome from '../PreCreditApplication/Welcome'
import LoanAmountForm from '../PreCreditApplication/LoanAmountForm'
import LoanPurposeForm from '../PreCreditApplication/LoanPurposeForm'
import ServiceProviderForm from '../PreCreditApplication/ServiceProviderForm'
import { useAppDispatch, useAppSelector } from '../../data/store'
import {
  creditApplicationActions,
  creditApplicationEffects,
  creditApplicationSelectors,
} from '../../data/store/CreditApplicationStore'
import PersonalInformationForm from '../PreCreditApplication/PersonalInformationForm'
import ContactInformationForm from '../PreCreditApplication/ContactInformationForm'
import AddressForm from '../PreCreditApplication/AddressForm'
import EmploymentForm from '../PreCreditApplication/EmploymentForm'
import Review from '../PreCreditApplication/Review'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { MerchantDto } from '../../data/types/Merchant'
import Loader from '../components/Loader'
import { StepProgressIcon, StepperFormFooter } from '../components'
import {
  Applicant,
  ApplicantSchema,
  CreditApplication,
  CreditApplicationSchema,
} from '../../data/schemas/PrequalificationSchema'
import { Enum } from '../../data/types/constants'
import { scrollToTop } from '../utils/utils'

import { reportErrorToServer } from '../../error-logger'
import { trackingEvents } from '../utils/trackingEvents'

export type dynamicObject = { [k: string]: unknown }

type Props = {
  isCoapplicant?: boolean
}

function InitialFinancingInformationContainer({ isCoapplicant }: Props) {
  const { merchantId, id } = useParams()
  const { i18n, t } = useTranslation()
  const dispatch = useAppDispatch()
  const dispatchEffect = useSideEffect()
  const location = useLocation()
  const loanAmountState: number = location?.state?.loanAmount ? location.state.loanAmount : null
  const stepState: number = location?.state?.step ? parseInt(location.state.step, 10) : 0
  const navigate = useNavigate()

  const [step, setStep] = React.useState<number>(isCoapplicant ? 1 : 0)
  const [creditApplication, setCreditApplication] = React.useState<CreditApplication>({
    ...CreditApplicationSchema.getDefault(),
    requestedLoanAmount: loanAmountState,
    applicant: {
      ...CreditApplicationSchema.getDefault().applicant,
      languageId: i18n.language === 'fr' ? 0 : 1,
    },
    coApplicant: isCoapplicant
      ? // prettier-ignore
        {
          ...ApplicantSchema.getDefault(),
          relationWithApplicant: Enum.ERelationToApplicant.Spouse,
          languageId: i18n.language === 'fr' ? 0 : 1,
        } as Applicant
      : null,
  } as CreditApplication)

  const baseUrls = ['', 'personal-information', 'contact', 'address', 'employment', 'review']
  const [stepUrls, setStepUrls] = React.useState(
    isCoapplicant ? baseUrls : ['', 'loan-amount', 'loan-purpose', 'service-provider'].concat(baseUrls.slice(1)),
  )

  const filteredCreditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const merchantIsLoading = useAppSelector(creditApplicationSelectors.MerchantIsLoading)
  const isIfinanceMerchant = Number(merchantId) === Number(process.env.REACT_APP_IFINANCE_MERCHANT_ID)
  const isMedicardMerchant = Number(merchantId) === Number(process.env.REACT_APP_MEDICARD_MERCHANT_ID)

  React.useEffect(() => {
    setCreditApplication((creditApp) => {
      const newData = { ...creditApp }
      const languageId = i18n.language === 'fr' ? 0 : 1
      if (merchantId) newData.applicant = { ...newData.applicant, languageId }
      else if (newData.coApplicant) newData.coApplicant = { ...newData.coApplicant, languageId }
      return newData
    })
  }, [i18n.language])

  React.useEffect(() => {
    if (merchantId) {
      dispatchEffect(creditApplicationEffects.getMerchant(merchantId))
        .then((merchant: MerchantDto) => {
          setCreditApplication({
            ...creditApplication,
            merchantId: merchant.id,
            loanPurposeId: !isIfinanceMerchant ? merchant.serviceCategory : '',
          })

          if (!isIfinanceMerchant && !isMedicardMerchant)
            setStepUrls((urls) => urls.filter((value) => value !== 'loan-purpose' && value !== 'service-provider'))
          else {
            setStepUrls(() =>
              isCoapplicant
                ? baseUrls
                : ['', 'loan-amount', 'loan-purpose', 'service-provider'].concat(baseUrls.slice(1)),
            )
          }

          if (stepState !== 0) setStep(stepState)
        })
        .catch((e) => {
          reportErrorToServer(e)
          navigate('/notFound', { replace: true })
        })
    }

    if (id && isCoapplicant) dispatchEffect(creditApplicationEffects.getFilteredCreditApplication(id))
  }, [merchantId, id])

  const goBackToPrevious = () => {
    if (window.location.pathname.includes('/coapplicant')) {
      navigate(`/creditApplication/${id as string}`, { replace: true })
    } else {
      navigate('/', { replace: true })
    }
  }

  React.useLayoutEffect(() => {
    scrollToTop()
  })

  React.useEffect(() => {
    navigate(`${stepUrls[step]}`)
  }, [step])

  const gotoStep = (stepPath: string) => {
    setStep(stepUrls.indexOf(stepPath))
  }

  const nextStep = () => {
    setStep((prev) => prev + 1)
  }

  const getStarted = () => {
    trackingEvents.getStarted({})
    setStep((prev) => prev + 1)
  }

  const goBack = () => {
    if (step > 1) setStep((prev) => prev - 1)
    else goBackToPrevious()
  }

  React.useEffect(() => {
    window.onpopstate = () => {
      goBack()
    }
  })

  React.useEffect(() => {
    dispatch(creditApplicationActions.setCreditApplication(creditApplication))
  }, [creditApplication])

  const getFooter = (handleBack: () => void, disableNext?: boolean | undefined) => (
    <StepperFormFooter
      disableBack={!isCoapplicant ? step === 1 : false}
      currentStep={step}
      disableNext={disableNext}
      back={handleBack}
      stepLength={isCoapplicant ? stepUrls.length : stepUrls.length - 1}
    />
  )

  const progresssion = <StepProgressIcon isCoapplicant={isCoapplicant} currentStep={step} />

  const updateCreditApplicationData = (data: dynamicObject) => {
    setCreditApplication((creditApp) => {
      return { ...creditApp, ...data } as CreditApplication
    })
  }

  const updateApplicantData = (applicantData: dynamicObject) => {
    setCreditApplication((creditApp) => {
      const applicant = isCoapplicant ? creditApp.coApplicant : creditApp.applicant
      const updateData = {
        ...creditApp,
        [isCoapplicant ? 'coApplicant' : 'applicant']: { ...applicant, ...applicantData },
      } as CreditApplication

      const otherIncomes = isCoapplicant ? updateData.coApplicant?.otherIncomes : updateData.applicant?.otherIncomes

      if (
        otherIncomes &&
        otherIncomes?.length > 0 &&
        (otherIncomes[0].typeId === null || otherIncomes[0].annualAmount === null)
      )
        if (isCoapplicant && updateData.coApplicant) updateData.coApplicant.otherIncomes = []
        else updateData.applicant.otherIncomes = []
      return updateData
    })
  }

  const updateBeneficiaryData = (data: dynamicObject) => {
    setCreditApplication((creditApp) => {
      const updateCreditApp = {
        ...creditApp,
        ...data,
      }
      return updateCreditApp
    })
  }

  const handleLoanPurposeFormSubmit = (data: dynamicObject) => {
    updateCreditApplicationData(data)
    const selectedMerchantId = data.merchantId as string
    if (
      selectedMerchantId !== null &&
      selectedMerchantId.split('*')[1] !== process.env.REACT_APP_IFINANCE_MERCHANT_ID?.toString()
    ) {
      const newLocation = `../../${selectedMerchantId.split('*')[1]}/personal-information`
      navigate(newLocation, { replace: true, state: { loanAmount: creditApplication.requestedLoanAmount, step: 2 } })
    } else {
      nextStep()
    }
  }

  const handleFormSubmit = (data: dynamicObject) => {
    updateCreditApplicationData(data)
    nextStep()
  }

  const handleFormBack = (data: dynamicObject) => {
    updateCreditApplicationData(data)
    goBack()
  }

  const handleFormSubmitApplicantData = (data: dynamicObject) => {
    updateApplicantData(data)
    updateBeneficiaryData(data)
    nextStep()
  }

  const handleFormBackApplicantData = (data: dynamicObject) => {
    updateApplicantData(data)
    updateBeneficiaryData(data)
    const currentMerchantId = creditApplication.merchantId
    const ifinanceMerchantId = process.env.REACT_APP_IFINANCE_MERCHANT_ID?.toString()
    if (!isCoapplicant && currentMerchantId !== null && currentMerchantId.split('*')[1] !== ifinanceMerchantId) {
      updateCreditApplicationData({ merchantId: `alis*${ifinanceMerchantId}`, merchantName: '' })
      const newLocation = `../../${ifinanceMerchantId}/loan-purpose`
      navigate(newLocation, { replace: true, state: { loanAmount: creditApplication.requestedLoanAmount, step: 2 } })
    } else {
      goBack()
    }
  }

  return (
    <>
      {merchantIsLoading && <Loader description="" title={t('common.withYouShortly')} />}

      {!merchantIsLoading && (
        <Routes>
          <Route path="/" element={<Welcome onSubmit={getStarted} />} />
          <Route
            path="/loan-amount"
            element={
              <LoanAmountForm
                stepProgression={progresssion}
                getFooter={getFooter}
                handleBack={handleFormBack}
                creditApplication={creditApplication}
                onFormSubmitted={handleFormSubmit}
              />
            }
          />
          <Route
            path="/loan-purpose"
            element={
              <LoanPurposeForm
                stepProgression={progresssion}
                creditApplication={creditApplication}
                getFooter={getFooter}
                handleBack={handleFormBack}
                onFormSubmitted={handleFormSubmit}
              />
            }
          />
          <Route
            path="/service-provider"
            element={
              <ServiceProviderForm
                stepProgression={progresssion}
                getFooter={getFooter}
                handleBack={handleFormBack}
                creditApplication={creditApplication}
                onFormSubmitted={handleLoanPurposeFormSubmit}
              />
            }
          />
          <Route
            path="/personal-information"
            element={
              <PersonalInformationForm
                isCoapplicant={isCoapplicant}
                stepProgression={progresssion}
                currentApplicant={
                  isCoapplicant ? (creditApplication.coApplicant as Applicant) : creditApplication.applicant
                }
                onFormSubmitted={handleFormSubmitApplicantData}
                getFooter={getFooter}
                handleBack={handleFormBackApplicantData}
                loanPurposeId={creditApplication.loanPurposeId}
              />
            }
          />
          <Route
            path="/contact"
            element={
              <ContactInformationForm
                onFormSubmitted={handleFormSubmitApplicantData}
                stepProgression={progresssion}
                getFooter={getFooter}
                handleBack={handleFormBackApplicantData}
                currentApplicant={
                  isCoapplicant ? (creditApplication.coApplicant as Applicant) : creditApplication.applicant
                }
              />
            }
          />
          <Route
            path="/address"
            element={
              <AddressForm
                isCoapplicant={isCoapplicant}
                stepProgression={progresssion}
                currentApplicant={
                  isCoapplicant ? (creditApplication.coApplicant as Applicant) : creditApplication.applicant
                }
                applicantProvince={filteredCreditApplication?.applicantProvince as string}
                onFormSubmitted={handleFormSubmitApplicantData}
                getFooter={getFooter}
                handleBack={handleFormBackApplicantData}
              />
            }
          />
          <Route
            path="/employment"
            element={
              <EmploymentForm
                onFormSubmitted={handleFormSubmitApplicantData}
                currentApplicant={
                  isCoapplicant ? (creditApplication.coApplicant as Applicant) : creditApplication.applicant
                }
                stepProgression={progresssion}
                getFooter={getFooter}
                handleBack={handleFormBackApplicantData}
              />
            }
          />
          <Route
            path="/review"
            element={
              <Review
                creditApplication={creditApplication}
                stepProgression={progresssion}
                isCoapplicant={isCoapplicant}
                gotoStep={gotoStep}
              />
            }
          />
          <Route path="*" element={<Navigate to="/notFound" />} />
        </Routes>
      )}
    </>
  )
}

InitialFinancingInformationContainer.defaultProps = {
  isCoapplicant: false,
}

export default InitialFinancingInformationContainer
